export type Direction = "up" | "down" | "left" | "right";

export interface RectLike {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface NavCandidate {
  id: string;
  rect: RectLike;
}

// How much we penalise the off-axis component of a candidate's offset. A
// value of 2 keeps the search inside a tight cone around the arrow axis: a
// neighbour twice as far off-axis as on-axis costs the same as one four
// times as far on-axis.
const OFF_AXIS_PENALTY = 2;

function center(rect: RectLike): { x: number; y: number } {
  return {
    x: (rect.left + rect.right) / 2,
    y: (rect.top + rect.bottom) / 2,
  };
}

// Find the next focus target in the given direction. Candidates must lie
// inside the 90° cone facing that direction (off-axis distance ≤ on-axis
// distance) and are then ranked by `on-axis + 2 * off-axis`. Returns null
// when nothing qualifies — the caller treats that as "stay where you are".
export function pickNearestInDirection(
  candidates: NavCandidate[],
  currentId: string,
  direction: Direction,
): string | null {
  const current = candidates.find((c) => c.id === currentId);
  if (!current) return null;
  const origin = center(current.rect);

  let best: { id: string; score: number } | null = null;

  for (const candidate of candidates) {
    if (candidate.id === currentId) continue;
    const c = center(candidate.rect);
    const dx = c.x - origin.x;
    const dy = c.y - origin.y;

    let primary: number;
    let offAxis: number;
    switch (direction) {
      case "right":
        if (dx <= 0 || Math.abs(dy) > dx) continue;
        primary = dx;
        offAxis = Math.abs(dy);
        break;
      case "left":
        if (dx >= 0 || Math.abs(dy) > -dx) continue;
        primary = -dx;
        offAxis = Math.abs(dy);
        break;
      case "down":
        if (dy <= 0 || Math.abs(dx) > dy) continue;
        primary = dy;
        offAxis = Math.abs(dx);
        break;
      case "up":
        if (dy >= 0 || Math.abs(dx) > -dy) continue;
        primary = -dy;
        offAxis = Math.abs(dx);
        break;
    }

    const score = primary + OFF_AXIS_PENALTY * offAxis;
    if (best === null || score < best.score) {
      best = { id: candidate.id, score };
    }
  }

  return best ? best.id : null;
}

// First-press entry point: pick the leftmost candidate, breaking ties by
// topmost position. Prioritising left over top matches the mental model
// of "first task in the first list" on a masonry layout, where listless
// tasks (no list header above them) can otherwise sit visually higher
// than the first task of the leftmost list column.
export function pickInitialFocus(candidates: NavCandidate[]): string | null {
  if (candidates.length === 0) return null;
  let best = candidates[0];
  for (const c of candidates) {
    if (c.rect.left < best.rect.left - 1) {
      best = c;
    } else if (
      Math.abs(c.rect.left - best.rect.left) <= 1 &&
      c.rect.top < best.rect.top
    ) {
      best = c;
    }
  }
  return best.id;
}
